import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../services/api";
import PublicNav from "../components/PublicNav";

function parseChartDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dmyMatch = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatChartDateParts(value) {
  const parsed = parseChartDate(value);
  if (!parsed) {
    return {
      weekdayLong: "Invalid date",
      weekdayShort: "--",
      day: "--",
      month: "--",
      fullDate: String(value || "-"),
    };
  }
  return {
    weekdayLong: parsed.toLocaleString("en-IN", { weekday: "long" }),
    weekdayShort: parsed.toLocaleString("en-IN", { weekday: "short" }),
    day: parsed.getDate(),
    month: parsed.toLocaleString("en-IN", { month: "short" }),
    fullDate: parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
  };
}

function StarlineChartPage() {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState({ market: null, slots: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [visibleDayCount, setVisibleDayCount] = useState(90);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    setLoading(true);
    setVisibleDayCount(90);
    API.get(`/charts/starline/${marketId}`)
      .then((res) => setPayload(res.data || { market: null, slots: [], rows: [] }))
      .catch(() => setPayload({ market: null, slots: [], rows: [] }))
      .finally(() => setLoading(false));
  }, [marketId]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const compact = viewportWidth <= 640;
  const slotCount = Math.max(1, (payload.slots || []).length);
  const gridTemplateColumns = compact ? `40px repeat(${slotCount}, minmax(0, 1fr))` : `78px repeat(${slotCount}, minmax(54px, 1fr))`;

  const groupedRows = useMemo(() => {
    const map = new Map();
    (payload.rows || []).forEach((row) => {
      const key = row.chart_date;
      if (!map.has(key)) map.set(key, {});
      map.get(key)[String(row.result_hour || "").trim()] = row;
    });
    return Array.from(map.entries()).sort((left, right) => (left[0] < right[0] ? 1 : -1));
  }, [payload.rows]);

  const visibleGroupedRows = useMemo(
    () => groupedRows.slice(0, visibleDayCount),
    [groupedRows, visibleDayCount]
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/charts");
  };

  return (
    <div style={page}>
      <PublicNav />
      <div style={subHeader}>
        <button type="button" style={backButton} onClick={handleBack}><ArrowLeft size={20} /></button>
        <h1 style={title}>{payload.market?.name || "Starline Chart"}</h1>
        <div style={rangeChip}><CalendarDays size={16} />{groupedRows.length}d</div>
      </div>

      <main style={main}>
        {loading ? <div style={loadingState}>Loading starline chart...</div> : null}

        {!loading && groupedRows.length ? <div style={tableShell}>
          {compact ? (
            <div style={mobileCardList}>
              {visibleGroupedRows.map(([date, byHour]) => {
                const dateParts = formatChartDateParts(date);
                return (
                  <section key={date} style={mobileDayCard}>
                    <div style={mobileDayHeader}>
                      <div>
                        <strong style={mobileDayTitle}>{dateParts.weekdayLong}</strong>
                        <div style={mobileDaySub}>{dateParts.fullDate}</div>
                      </div>
                    </div>
                    <div style={mobileHourGrid}>
                      {(payload.slots || []).map((slot) => {
                        const item = byHour[String(slot.time_slot || "").trim()];
                        return (
                          <div key={`${date}-${slot.time_slot}`} style={mobileHourCell}>
                            <span style={mobileHourTime}>{slot.time_slot}</span>
                            <span style={mobileHourPana}>{item?.pana || "---"}</span>
                            <span style={digitBubble}>{item?.digit || "-"}</span>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <div style={tableInner}>
              <div style={{ ...headerRow, gridTemplateColumns }}>
                <div style={dateHead}>Date</div>
                {(payload.slots || []).map((slot) => (
                  <div key={slot.time_slot} style={slotHead}>{slot.time_slot}</div>
                ))}
              </div>

              {visibleGroupedRows.map(([date, byHour]) => {
                const dateParts = formatChartDateParts(date);
                return (
                  <div key={date} style={{ ...row, gridTemplateColumns }}>
                    <div style={dateCol}>
                      <strong style={dayText}>{dateParts.weekdayShort}</strong>
                      <b style={dateNumber}>{dateParts.day}</b>
                      <span style={monthText}>{dateParts.month}</span>
                    </div>

                    {(payload.slots || []).map((slot) => {
                      const item = byHour[String(slot.time_slot || "").trim()];
                      return (
                        <div key={`${date}-${slot.time_slot}`} style={cell}>
                          <span style={panaText}>{item?.pana || "---"}</span>
                          <span style={digitBubble}>{item?.digit || "-"}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div> : null}
        {!loading && visibleGroupedRows.length < groupedRows.length ? (
          <button type="button" style={loadMoreButton} onClick={() => setVisibleDayCount((count) => count + 90)}>
            Load older chart
          </button>
        ) : null}
        {!loading && !groupedRows.length ? <div style={emptyState}>No starline chart uploaded yet.</div> : null}
      </main>
    </div>
  );
}

export default StarlineChartPage;

const page = { minHeight: "100vh", background: "#f8fafc", fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" };
const subHeader = { height: 52, background: "#fff", borderBottom: "1px solid #e5e7eb", display: "grid", gridTemplateColumns: "44px 1fr auto", alignItems: "center", gap: 12, padding: "0 12px" };
const backButton = { width: 40, height: 40, border: 0, borderRadius: 14, background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center" };
const title = { margin: 0, fontSize: "clamp(18px, 6vw, 22px)", fontWeight: 900, textAlign: "center" };
const rangeChip = { minWidth: 72, height: 42, borderRadius: 14, background: "#eef4ff", color: "#2563eb", fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 };
const main = { padding: 10 };
const tableShell = { background: "#fff", borderRadius: 20, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 12px 28px rgba(15,23,42,.05)" };
const tableInner = { overflowX: "auto" };
const mobileCardList = { display: "grid", gap: 10, padding: 10 };
const mobileDayCard = { border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff", overflow: "hidden" };
const mobileDayHeader = { padding: "10px 12px", borderBottom: "1px solid #eef2f7", background: "linear-gradient(135deg,#f8fbff,#f1f5f9)" };
const mobileDayTitle = { fontSize: 14, color: "#0f172a", fontWeight: 900 };
const mobileDaySub = { marginTop: 3, fontSize: 11, color: "#64748b", fontWeight: 700 };
const mobileHourGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, padding: 10 };
const mobileHourCell = { minHeight: 64, borderRadius: 14, border: "1px solid #e5e7eb", background: "#f8fafc", display: "grid", justifyItems: "center", alignContent: "center", gap: 4, padding: "8px 4px", textAlign: "center" };
const mobileHourTime = { fontSize: 10, color: "#64748b", fontWeight: 800, lineHeight: 1.1 };
const mobileHourPana = { fontSize: 12, color: "#1f2937", fontWeight: 900, letterSpacing: ".02em" };
const headerRow = { display: "grid" };
const dateHead = { padding: "10px 2px", background: "#0f172a", color: "#fff", fontWeight: 900, textAlign: "center", fontSize: "10px", lineHeight: 1.1 };
const slotHead = { padding: "10px 2px", background: "#0f172a", color: "#fff", fontWeight: 900, textAlign: "center", borderLeft: "1px solid rgba(255,255,255,.06)", fontSize: "9px", lineHeight: 1.15 };
const row = { display: "grid", borderTop: "1px solid #eef2f7" };
const dateCol = { minHeight: 68, background: "#f8fbff", display: "grid", alignContent: "center", justifyItems: "center", gap: 1, padding: "4px 0" };
const dayText = { fontSize: 10, color: "#64748b" };
const dateNumber = { fontSize: 18, color: "#0f172a", lineHeight: 1 };
const monthText = { fontSize: 9, color: "#94a3b8" };
const cell = { minHeight: 68, borderLeft: "1px solid #eef2f7", display: "grid", alignContent: "center", justifyItems: "center", gap: 5, padding: "6px 1px" };
const panaText = { fontSize: 10, fontWeight: 900, color: "#1f2937", lineHeight: 1 };
const digitBubble = { minWidth: 18, height: 18, padding: "0 4px", borderRadius: 999, background: "#2563eb", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 10 };
const emptyState = { marginTop: 12, background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 18, padding: "20px 16px", textAlign: "center", color: "#64748b", fontWeight: 700 };
const loadingState = { ...emptyState, border: "1px solid #dbeafe", color: "#2563eb", background: "linear-gradient(135deg,#ffffff,#eff6ff)" };
const loadMoreButton = { width: "100%", minHeight: 48, marginTop: 12, border: 0, borderRadius: 16, background: "linear-gradient(90deg,#2563eb,#7c3aed)", color: "#fff", fontWeight: 900, fontSize: 15, boxShadow: "0 14px 24px rgba(37,99,235,.18)" };
